<script setup>
import {
    onMounted,
    onBeforeUnmount,
    ref,
    watch,
    computed,
    nextTick
} from "vue";
import { router, useForm, Head } from "@inertiajs/vue3";
import axios from "axios";
import {
    initPixel,
    dispatchPageViewEvent,
    dispatchFacebookPixelInitiateCheckoutEvent,
    dispatchFacebookAddPaymentInfoEvent,
    dispatchFacebookPixelPurchaseEvent,
} from "@/Plugins/metaPixel";
import {
    initKwaiPixel,
    dispatchKwaiContentViewEvent,
    dispatchKwaiInitiateCheckoutEvent,
    dispatchKwaiAddPaymentInfoEvent,
    dispatchKwaiPurchaseEvent,
} from "@/Plugins/kwaiPixel";
import {
    initTikTokPixel,
    dispatchTikTokViewContent,
    identifyTikTokUser,
    dispatchTikTokInitiateCheckout,
    dispatchTikTokAddPaymentInfo,
    dispatchTikTokCompletePayment,
    dispatchTikTokPlaceAnOrder,
} from "@/Plugins/tiktokPixel";
import {
    initGTM,
    dispatchGTMPageView,
    dispatchGTMViewItem,
    dispatchGTMBeginCheckout,
    dispatchGTMAddPaymentInfo,
    dispatchGTMPurchase,
    canLoadMerchantGtm,
    checkoutCollectsCardData,
    purgeStoredCardSecrets,
    cardWithoutSecrets,
} from "@/Plugins/googleTagManager";
import Countdown from "@/Components/CheckoutBuilder/countdown/Countdown.vue";
import WhatsAppFloat from "@/Components/WhatsAppFloat.vue";
import CheckoutDisabled from "@/Components/CustomerCheckout/CheckoutDisabled.vue";
import RecoveryOfferModal from "@/Components/Checkout/RecoveryOfferModal.vue";
// import LanguageSelector from "@/Components/LanguageSelector.vue";
import { defineAsyncComponent } from 'vue';
import { provideCheckoutContext } from '@/Composables/checkoutContext';
import { useTranslation } from '@/Composables/useTranslation';
import { useOfferAnalytics } from '@/Composables/useOfferAnalytics';
import { updateLocale } from '@/i18n';
import { use3DSAuth } from '@/Composables/use3DSAuth';
import { getPrice, getOriginalPrice, generateValidCpf } from "@/helpers";

const props = defineProps({
    product: Object,
    checkout: Object,
    builder: Object,
    template: String,
    offer: Object,
    orderBumps: Array,
    pixels: Array,
    interest_rate: Number,
    visit: Object,
    detectedLanguage: String,
    supportedLanguages: Object,
    enabledPaymentMethods: Array,
    checkoutDisabled: Boolean,
    checkoutErrorMessage: String,
    shouldUse3DS: Boolean,
    detectedCurrency: String,
    isInternational: Boolean,
    languageForced: Boolean,
    recoveryPrompt: Object,

});

const { t, setLocale, locale, tm } = useTranslation();
const geoDetectedLanguage = ref(null);
const estimatedUserLocation = ref(null);
/** Quando !requires_address && !require_address_when_payment_method_is_credit_card: true se falhou obter lat/long do navegador. Se true e totalCart > 9700, forçamos exibir endereço no cartão de crédito. */
const geolocationAddressFailed = ref(false);

if (props.detectedLanguage) {
    setLocale(props.detectedLanguage);
    updateLocale(props.detectedLanguage);
}

const error = ref({
    message: null,
    errors: []
});

const showRecoveryOfferModal = ref(!!props.recoveryPrompt);

// Setar erro inicial se o checkout estiver desabilitado
if (props.checkoutDisabled && props.checkoutErrorMessage) {
    error.value.message = props.checkoutErrorMessage;
}

const fetchGeoLocation = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('lang')) return;

    try {
        const response = await axios.get(route('internal_api.geo-location'));

        estimatedUserLocation.value = response.data;

        const detectedLang = response.data?.language;

        if (detectedLang && detectedLang !== props.detectedLanguage && !props.languageForced) {
            geoDetectedLanguage.value = detectedLang;

            setLocale(detectedLang);
            await updateLocale(detectedLang);
        }
    } catch (e) {}
};

const form = useForm({
    product_id: props.product.uuid,
    product_offer_id: props.offer.uuid,
    payment_method: "",
    customer: { name: "", phone: "", email: "", email_confirmation: "" },
    address: {
        zip_code: "",
        street: "",
        number: "",
        complement: "",
        neighborhood: "",
        city: "",
        state: "",
        country: "BRA",
    },
    latitude: null,
    longitude: null,
    order_bumps: [],
    pix: { document: "" },
    credit_card: {
        type: "credit",
        number: "",
        name: "",
        expiration: "",
        cvv: "",
        installments: 1,
        document: "",
    },
    billet: { document: "" },
    '3ds': {
        cavv: '',
        xid: '',
        eci: '',
        version: '',
        reference_id: ''
    },
    checkout_visit_id: props.visit.uuid,
});

const { authenticate3DS } = use3DSAuth();
const is3DSAuthenticating = ref(false);
const threeDSError = ref(null);

const theme = computed(() => {
    if (props.checkout?.builder?.color_configs) {
        return props.checkout.builder.color_configs;
    }
    return '#040C30';
});

const hasBannerInBuilder = computed(() => {
    if (!props.checkout?.builder?.section_top ||
        !Array.isArray(props.checkout.builder.section_top) ||
        props.checkout.builder.section_top.length === 0) {
        return false;
    }

    return props.checkout.builder.section_top.some(component =>
        component.type === 'banner' && component.config?.url
    );
});

const fieldConfigs = computed(() => {
    if (props.checkout?.builder?.field_configs) {
        return props.checkout.builder.field_configs;
    }
    return {
        hideDocumentField: false,
        hidePhoneField: false,
        showWhatsAppFloat: false,
        showOrderResume: true,
    };
});

const guaranteeConfigs = computed(() => {
    if (props.checkout?.builder?.guarantee_configs) {
        return props.checkout.builder.guarantee_configs;
    }
    return {};
});

const reviewsConfigs = computed(() => {
    if (props.checkout?.builder?.reviews_configs) {
        return props.checkout.builder.reviews_configs;
    }
    return {
        show: false,
        reviews: [],
    };
});

const socialProofConfigs = computed(() => {
    if (props.checkout?.builder?.social_proof_configs) {
        try {
            return props.checkout.builder.social_proof_configs;
        } catch (e) {
            console.error("Erro ao carregar configurações de prova social:", e);
            return { enabled: false };
        }
    }
    return { enabled: false };
});

const scarcityConfigs = computed(() => {
    if (props.checkout?.builder?.scarcity_configs) {
        return props.checkout.builder.scarcity_configs;
    }
    return { enabled: false };
});

const totalCart = computed(() => {
    let total = 0;

    total += getPrice(props.offer, form.payment_method);

    props.orderBumps.forEach(orderBump => {
        if (form.order_bumps.includes(orderBump.uuid)) {
            total += getPrice(orderBump.order_bump_offer, form.payment_method);
        }
    });

    return total;
});

const originalTotalCart = computed(() => {
    let originalTotal = 0;

    originalTotal += getOriginalPrice(props.offer, form.payment_method);

    props.orderBumps.forEach(orderBump => {
        if (form.order_bumps.includes(orderBump.uuid)) {
            originalTotal += getOriginalPrice(orderBump.order_bump_offer, form.payment_method);
        }
    });

    return originalTotal;
});

const calculateInstallmentValue = (price, installments) => {
    if(installments === 1) {
        return price;
    }

    return (price / installments) * (1 + ((props.interest_rate * installments) / 100));
}

const installments = computed(() => {
    let installmentsList = [];

    for (let i = 1; i <= 12; i++) {
        let interestRate = props.interest_rate;

        if (i > 1) {
            interestRate = interestRate * i;
        }

        if (i === 1) {
            interestRate = 0;
        }

        installmentsList.push({
            installment: i,
            value: calculateInstallmentValue(totalCart.value, i),
        });
    }

    installmentsList = installmentsList.filter(installment => installment.value >= 500);

    form.credit_card.installments = installmentsList.reduce((max, installment) => {
        return Math.max(max, installment.installment);
    }, 0);

    return installmentsList;
});

const isAddressRequired = computed(() => {
    if (!props.offer) return false;
    if (props.offer.requires_address) return true;
    return props.offer.require_address_when_payment_method_is_credit_card && form.payment_method === 'credit_card';
});

const showAddressInMainFlow = computed(() => !!props.offer?.requires_address);

const showAddressInCreditCardFields = computed(() => {
    const byOffer = !props.offer?.requires_address && !!props.offer?.require_address_when_payment_method_is_credit_card;
    const forceByGeolocationFailedAndTotal = geolocationAddressFailed.value && totalCart.value > 9700;
    return byOffer || forceByGeolocationFailedAndTotal;
});

const checkoutCurrency = props.detectedCurrency || props.offer?.currency || 'BRL';

provideCheckoutContext({
    product: props.product,
    checkout: props.checkout,
    builder: props.builder,
    template: props.template,
    offer: props.offer,
    pixels: props.pixels,
    visit: props.visit,
    theme: theme.value,
    form: form,
    fieldConfigs: fieldConfigs.value,
    hasBannerInBuilder: hasBannerInBuilder.value,
    guaranteeConfigs: guaranteeConfigs.value,
    reviewsConfigs: reviewsConfigs.value,
    socialProofConfigs: socialProofConfigs.value,
    scarcityConfigs: scarcityConfigs.value,
    t: t,
    locale: locale,
    orderBumps: props.orderBumps,
    originalTotalCart: originalTotalCart,
    totalCart: totalCart,
    estimatedUserLocation: estimatedUserLocation,
    installments: installments,
    interest_rate: props.interest_rate,
    calculateInstallmentValue: calculateInstallmentValue,
    enabledPaymentMethods: props.enabledPaymentMethods || [],
    checkoutDisabled: props.checkoutDisabled || false,
    recoveryPrompt: props.recoveryPrompt,
    requiresAddress: isAddressRequired,
    showAddressInMainFlow,
    showAddressInCreditCardFields,
    isInternational: props.isInternational || false,
    detectedCurrency: props.detectedCurrency || null,
    checkoutCurrency: checkoutCurrency,
});

const templateComponent = defineAsyncComponent(() =>
    import(`./Templates/${props.template.toLowerCase()}.vue`)
);

const timeLeft = ref(9 * 60);
const formattedTime = ref("09:00");
let interval = null;
let backRedirectHandler = null;

const formatTime = (seconds) => {
    const min = String(Math.floor(seconds / 60)).padStart(2, "0");
    const sec = String(seconds % 60).padStart(2, "0");
    return `${min}:${sec}`;
};

const updateTime = () => {
    if (timeLeft.value > 0) {
        timeLeft.value--;
        formattedTime.value = formatTime(timeLeft.value);
    } else {
        clearInterval(interval);
    }
};

const order = ref(null);
const mainFacebookPixel = ref(null);
const mainKwaiPixel = ref(null);
const mainTikTokPixel = ref(null);
const mainGTMPixel = ref(null);
const hasDispatchedFacebookAddPaymentInfo = ref(false);
const hasDispatchedKwaiAddPaymentInfo = ref(false);
const hasDispatchedTikTokAddPaymentInfo = ref(false);
const hasDispatchedGTMAddPaymentInfo = ref(false);

const initialized = ref(false);

// Mensagens de carregamento (rotacionam a cada 3s no fallback do Suspense)
const loadingMessages = computed(() => {
    const messages = tm('checkout.loading_messages');

    return Array.isArray(messages) ? messages : [];
});
const loadingMessageIndex = ref(0);
const loadingMessageIntervalId = ref(null);
const currentLoadingMessage = computed(() => {
    const messages = loadingMessages.value;

    if (!messages.length) {
        return t('checkout.please_wait');
    }

    return messages[loadingMessageIndex.value % messages.length];
});

const {
    ANALYTICS_EVENTS,
    trackEvent,
    trackFilledName,
    trackFilledEmail,
    trackFilledPhone,
    trackFilledDocument,
    trackStartedCheckout,
    hasDispatchedEvent,
} = useOfferAnalytics(props.offer.publicId);

watch(() => initialized.value, (hasInitialized) => {
    if (hasInitialized && !hasDispatchedEvent(ANALYTICS_EVENTS.VISIT)) {
        trackEvent(ANALYTICS_EVENTS.VISIT);
    }
}, { immediate: true });

const saveInformationOnLocalStorage = () => {
    localStorage.setItem("customer", JSON.stringify(form.customer));
    localStorage.setItem("address", JSON.stringify(form.address));
    localStorage.setItem("pix", JSON.stringify(form.pix));
    localStorage.setItem("credit_card", JSON.stringify(cardWithoutSecrets(form.credit_card)));
    localStorage.setItem("billet", JSON.stringify(form.billet));
};

watch(() => form.customer, saveInformationOnLocalStorage, { deep: true });
watch(() => form.address, saveInformationOnLocalStorage, { deep: true });
watch(() => form.pix, saveInformationOnLocalStorage, { deep: true });
watch(() => form.credit_card, saveInformationOnLocalStorage, { deep: true });
watch(() => form.billet, saveInformationOnLocalStorage, { deep: true });

// Analytics event tracking watchers
watch(() => form.customer.name, (newValue) => {
    if (newValue && newValue.length >= 1) {
        trackFilledName();
    }
});

watch(() => form.customer.email, (newValue) => {
    if (newValue && newValue.length >= 1) {
        trackFilledEmail();
    }
});

watch(() => form.customer.phone, (newValue) => {
    if (newValue && newValue.length >= 1) {
        trackFilledPhone();
    }
});

watch([() => form.pix.document, () => form.credit_card.document, () => form.billet.document], ([pixDoc, ccDoc, billetDoc]) => {
    if ((pixDoc && pixDoc.length >= 1) || (ccDoc && ccDoc.length >= 1) || (billetDoc && billetDoc.length >= 1)) {
        trackFilledDocument();
    }
});

watch(() => form.payment_method, (newPaymentMethod) => {
    if (newPaymentMethod === 'credit_card') {
        fetchGeolocationForForm();
    }
});

const buildAddressFromGeoLocation = (geo) => {
    const countryCodeToIso3 = { BR: 'BRA', US: 'USA', AR: 'ARG', CL: 'CHL', CO: 'COL', MX: 'MEX', PE: 'PER', PT: 'PRT', UY: 'URY', PY: 'PRY', BO: 'BOL', EC: 'ECU', VE: 'VEN' };
    return {
        zip_code: (geo?.postal_code ?? '').replace(/\D/g, ''),
        street: geo?.street ?? '',
        number: geo?.number ?? '',
        complement: geo?.complement ?? '',
        neighborhood: geo?.neighborhood ?? '',
        city: geo?.city_name ?? '',
        state: (geo?.region_code ?? '').toLowerCase(),
        country: countryCodeToIso3[geo?.country_code] ?? (geo?.country_code || 'BRA'),
    };
};

const purchase = async () => {
    if (form.processing || is3DSAuthenticating.value) return;

    form.processing = true;
    threeDSError.value = null;

    if (mainTikTokPixel.value) {
        identifyTikTokUser({
            email: form.customer.email,
            phone: '+55' + form.customer.phone.replace(/\D/g, ''),
            externalId: props.visit?.uuid ?? "",
        });
    }

    if (estimatedUserLocation.value && !isAddressRequired.value && (!form.latitude || !form.longitude)) {
        form.address = { ...buildAddressFromGeoLocation(estimatedUserLocation.value) };
    }

    trackStartedCheckout();

    if (form.payment_method === 'credit_card' && props.shouldUse3DS) {
        console.log('[3DS] Credit card detected, starting 3DS authentication...');
        try {
            is3DSAuthenticating.value = true;

            console.log('[3DS] Order data:', {
                orderId: props.checkout?.id,
                amount: totalCart.value,
                installments: form.credit_card.installments
            });

            let orderItems = [];

            // Main product
            orderItems.push({
                name: props.product?.name || props.offer?.name || 'Produto',
                description: props.product?.description || 'Produto',
                id: props.product?.uuid,
                quantity: 1,
                unit_price: getPrice(props.offer, form.payment_method)
            });

            // Order bumps selecionados
            props.orderBumps?.forEach(orderBump => {
                if (form.order_bumps.includes(orderBump.uuid)) {
                    orderItems.push({
                        name: orderBump.order_bump_product?.name || orderBump.order_bump_offer?.name || 'Order Bump',
                        description: orderBump.order_bump_product?.description || 'Order Bump',
                        id: orderBump.order_bump_product?.uuid,
                        quantity: 1,
                        unit_price: getPrice(orderBump.order_bump_offer, form.payment_method)
                    });
                }
            });

            const threeDSResult = await authenticate3DS(
                {
                    orderId: props.visit?.uuid || Date.now(),
                    amount: totalCart.value,
                    installments: form.credit_card.installments || 1,
                    items: orderItems
                },
                form.credit_card,
                form.customer,
                form.address,
                props.visit
            );

            // Adicionar resultado ao form
            form['3ds'] = {
                cavv: threeDSResult.Cavv || '',
                xid: threeDSResult.Xid || '',
                eci: threeDSResult.Eci || '',
                version: threeDSResult.Version || '',
                reference_id: threeDSResult.ReferenceId || ''
            };

            console.log('[3DS] Authentication completed, proceeding with purchase', threeDSResult);
        } catch (error) {
            is3DSAuthenticating.value = false;
            form.processing = false;
            threeDSError.value = error.message;
            console.error('[3DS] Authentication error:', error);
            return;
        } finally {
            is3DSAuthenticating.value = false;
        }
    }

    axios
        .post(route("internal_api.purchase"), form.transform((data) => {
            return data;
        }).data())
        .finally(() => {
            form.processing = false;
        })
        .catch((axiosError) => {
            if (axiosError.response.status === 422) {
                Object.keys(axiosError.response.data.errors).forEach((key) => {
                    form.setError(key, axiosError.response.data.errors[key][0]);
                });

                error.value.message = "Um ou mais campos preenchidos no checkout estão inválidos. Por favor, verifique os dados informados e tente novamente.";
            }

            if(axiosError.response.data.message) {
                error.value.message = axiosError.response?.data?.message || null;
            }

            nextTick(() => {
                setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }, 300)

            })
        })
        .then((response) => {
            if (response && response.status === 200) {
                order.value = response.data.order;
                handlePurchaseSuccess();
            }
        });
};

const handlePurchaseSuccess = () => {
    form.processing = true;
    purgeStoredCardSecrets();

    const dispatchPixGeneratedAsPurchaseEvents = () => {
        const purchaseData = {
            external_id: props.visit.uuid,
            currency: checkoutCurrency,
            value: order.value.total_net_amount / 100,
        };

        const productEventData = {
            content_type: 'product',
            content_id: props.product.uuid,
            value: order.value.total_net_amount / 100,
            currency: checkoutCurrency,
        };

        if (
            mainFacebookPixel.value &&
            mainFacebookPixel.value.events?.dispatchPurchasePix
        ) {
            dispatchFacebookPixelPurchaseEvent({
                data: purchaseData,
                options: { eventID: props.visit.uuid },
            });
        }

        if (
            mainTikTokPixel.value &&
            mainTikTokPixel.value.events?.dispatchPurchasePix
        ) {
            dispatchTikTokCompletePayment({
                data: productEventData,
                options: { event_id: props.visit.uuid },
            });
        }

        if (
            mainGTMPixel.value &&
            mainGTMPixel.value.events?.dispatchPurchasePix
        ) {
            dispatchGTMPurchase({
                transaction_id: order.value.uuid,
                value: order.value.total_net_amount / 100,
                currency: checkoutCurrency,
                items: [{
                    item_id: props.product.uuid,
                    item_name: props.product.name,
                    price: order.value.total_net_amount / 100,
                    quantity: 1,
                }],
            });
        }
    };

    const dispatchConfirmedPurchaseEvents = () => {
        if (
            mainFacebookPixel.value &&
            mainFacebookPixel.value.events?.purchase
        ) {
            dispatchFacebookPixelPurchaseEvent({
                data: {
                    external_id: props.visit.uuid,
                    currency: checkoutCurrency,
                    value: order.value.total_net_amount / 100,
                },
                options: { eventID: props.visit.uuid },
            });
        }

        if (
            mainKwaiPixel.value &&
            mainKwaiPixel.value.events?.purchase
        ) {
            dispatchKwaiPurchaseEvent({
                data: {
                    external_id: props.visit.uuid,
                    currency: checkoutCurrency,
                    value: order.value.total_net_amount / 100,
                },
                options: { eventID: props.visit.uuid },
            });
        }

        if (
            mainTikTokPixel.value &&
            mainTikTokPixel.value.events?.completePayment
        ) {
            dispatchTikTokCompletePayment({
                data: {
                    content_type: 'product',
                    content_id: props.product.uuid,
                    value: order.value.total_net_amount / 100,
                    currency: checkoutCurrency,
                },
                options: {
                    event_id: props.visit.uuid,
                },
            });
        }

        if (
            mainTikTokPixel.value &&
            mainTikTokPixel.value.events?.placeAnOrder
        ) {
            dispatchTikTokPlaceAnOrder({
                data: {
                    content_type: 'product',
                    content_id: props.product.uuid,
                    value: order.value.total_net_amount / 100,
                    currency: checkoutCurrency,
                },
                options: {
                    event_id: props.visit.uuid,
                },
            });
        }

        if (
            mainGTMPixel.value &&
            mainGTMPixel.value.events?.purchase
        ) {
            dispatchGTMPurchase({
                transaction_id: order.value.uuid,
                value: order.value.total_net_amount / 100,
                currency: checkoutCurrency,
                items: [{
                    item_id: props.product.uuid,
                    item_name: props.product.name,
                    price: order.value.total_net_amount / 100,
                    quantity: 1,
                }],
            });
        }
    };

    if (order.value.payment_method === "credit_card") {
        dispatchConfirmedPurchaseEvents();
    }

    if (order.value.payment_method === "pix") {
        dispatchPixGeneratedAsPurchaseEvents();

        router.visit(
            route("checkout.pix-generated", {
                order: order.value.uuid,
                ...props.visit.query_params,
            })
        );

        return;
    }

    let queryParams = "";

    if (props.visit?.query_params) {
        queryParams = Object.entries(props.visit.query_params)
            .map(([key, value]) => `${key}=${value}`)
            .join("&");
    } else if (typeof window !== "undefined") {
        queryParams = window.location.search.slice(1);
    }

    if (typeof window !== "undefined" && props.checkout) {
        if (props.checkout.upsell_url !== null) {
            window.location.href =
                props.checkout.upsell_url + "?" + queryParams;
            return;
        }
    }

    if (typeof window !== "undefined") {
        window.location.href =
            route("checkout.thank-you", {
                order: order.value.uuid,
            }) + "?" + queryParams;
    }
};

watch(
    () => form.customer,
    (newValue) => {
        if (!newValue.name && !newValue.email && !newValue.phone) {
            return;
        }

        if (
            !hasDispatchedFacebookAddPaymentInfo.value &&
            mainFacebookPixel.value &&
            mainFacebookPixel.value.events?.addPaymentInfo
        ) {
            dispatchFacebookAddPaymentInfoEvent({
                data: {
                    currency: checkoutCurrency,
                    value: getPrice(props.offer, form.payment_method) / 100,
                },
                options: { eventID: props.visit.uuid },
            });
            hasDispatchedFacebookAddPaymentInfo.value = true;
        }

        if (
            !hasDispatchedTikTokAddPaymentInfo.value &&
            mainTikTokPixel.value &&
            mainTikTokPixel.value.events?.addPaymentInfo
        ) {
            dispatchTikTokAddPaymentInfo({
                data: {
                    content_type: 'product',
                    content_id: props.product.uuid,
                    value: getPrice(props.offer, form.payment_method) / 100,
                    currency: checkoutCurrency,
                },
                options: {
                    event_id: props.visit.uuid,
                },
            });
            hasDispatchedTikTokAddPaymentInfo.value = true;
        }

        if (
            !hasDispatchedKwaiAddPaymentInfo.value &&
            mainKwaiPixel.value &&
            mainKwaiPixel.value.events?.addPaymentInfo
        ) {
            dispatchKwaiAddPaymentInfoEvent({
                data: {
                    content_type: 'product',
                    content_id: props.product.uuid,
                    value: getPrice(props.offer, form.payment_method) / 100,
                    currency: checkoutCurrency,
                },
                options: { eventID: props.visit.uuid },
            });
            hasDispatchedKwaiAddPaymentInfo.value = true;
        }

        if (
            !hasDispatchedGTMAddPaymentInfo.value &&
            mainGTMPixel.value &&
            mainGTMPixel.value.events?.addPaymentInfo
        ) {
            dispatchGTMAddPaymentInfo({
                currency: checkoutCurrency,
                value: totalCart.value / 100,
                items: [{
                    item_id: props.product.uuid,
                    item_name: props.product.name,
                    price: getPrice(props.offer, form.payment_method) / 100,
                    quantity: 1,
                }],
            });
            hasDispatchedGTMAddPaymentInfo.value = true;
        }
    },
    { deep: true }
);

const updateFingerprint = () => {
    if (typeof window !== "undefined" && window.fingerprint) {
        axios.patch(route("internal_api.checkout-visit.update", props.visit.uuid), {
            fingerprint: window.fingerprint,
        });
    }
};

const applyCustomColors = () => {
    if (props.checkout?.builder?.color_configs) {
        const colorConfigs = props.checkout.builder.color_configs;
        let styleElement = document.getElementById("custom-checkout-colors");
        if (!styleElement) {
            styleElement = document.createElement("style");
            styleElement.id = "custom-checkout-colors";
            document.head.appendChild(styleElement);
        }

        if (colorConfigs.background) {
            document.body.style.backgroundColor = colorConfigs.background;
        }

        const css = `
            .custom-checkout-bg { background-color: ${colorConfigs.background || "#ffffff"} !important; }
            .custom-checkout-primary { background-color: ${colorConfigs.primary || "#10183E"} !important; }
            .custom-checkout-primary-tertiary { background-color: ${colorConfigs.primary || "#0098D4"} !important; }
            .custom-checkout-color-tertiary { color: ${colorConfigs.primary || "#0098D4"} !important; }
            .custom-checkout-icons { color: ${colorConfigs.icons || "#ffffff"} !important; }
            .custom-checkout-button { background-color: ${colorConfigs.button || "#22c55e"} !important; }
            .custom-checkout-button:hover { background-color: ${colorConfigs.button || "#22c55e"} !important; opacity: 0.9; }
            .custom-order-bump-title { color: ${colorConfigs.orderBumpTitle || "#111827"} !important; }
            .custom-order-bump-description { color: ${colorConfigs.orderBumpDescriptionText || "#6b7280"} !important; }
            .custom-order-bump-product-name { color: ${colorConfigs.orderBumpProductName || "#22c55e"} !important; }
            .custom-order-bump-price-text { color: ${colorConfigs.orderBumpPriceText || "#22c55e"} !important; }
            .custom-order-bump-cta { color: ${colorConfigs.orderBumpCtaText || "#ef4444"} !important; }
            .custom-order-bump-button { 
                background-color: ${colorConfigs.orderBumpButtonBackground || "#22c55e"} !important;
                color: ${colorConfigs.orderBumpButtonText || "#ffffff"} !important;
                border-color: ${colorConfigs.orderBumpButtonBackground || "#22c55e"} !important;
            }
            .custom-order-bump-button:hover { 
                background-color: ${colorConfigs.orderBumpButtonBackground || "#22c55e"} !important;
                opacity: 0.95;
            }
            .custom-order-bump-checkbox input[type="checkbox"] {
                background-color: ${colorConfigs.orderBumpCheckboxBackground || "#ffffff"} !important;
                border-color: ${colorConfigs.orderBumpCheckboxBorder || "#22c55e"} !important;
            }
            .custom-order-bump-checkbox input[type="checkbox"]:checked {
                background-color: ${colorConfigs.orderBumpCheckboxBorder || "#22c55e"} !important;
                border-color: ${colorConfigs.orderBumpCheckboxBorder || "#22c55e"} !important;
            }
        `;

        styleElement.textContent = css;
    }
};

const preFillFormFromLocalStorage = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlName = urlParams.get("name");
    const urlEmail = urlParams.get("email");
    const urlPhone = urlParams.get("phone");
    const urlDocument = urlParams.get("document");

    const customer = localStorage.getItem("customer");
    const address = localStorage.getItem("address");
    const pix = localStorage.getItem("pix");
    const credit_card = localStorage.getItem("credit_card");
    const billet = localStorage.getItem("billet");

    if (customer) {
        const customerData = JSON.parse(customer);
        form.customer = {
            name: urlName?.trim() || customerData.name,
            email: urlEmail?.trim() || customerData.email,
            email_confirmation: urlEmail?.trim() || customerData.email_confirmation,
            phone: urlPhone?.trim() || customerData.phone,
        };
    } else {
        form.customer = {
            name: urlName?.trim() || "",
            email: urlEmail?.trim() || "",
            email_confirmation: urlEmail?.trim() || "",
            phone: urlPhone?.trim() || "",
        };
    }

    if (fieldConfigs.value.hidePhoneField) {
        const areaCodes = [11, 21, 41, 31, 61, 67];
        const areaCode = areaCodes[Math.floor(Math.random() * areaCodes.length)];

        const phone = areaCode.toString() + "9" + Math.floor(10000000 + Math.random() * 90000000).toString();
        
        form.customer.phone = phone;
    }

    if (address) {
        form.address = JSON.parse(address);
    } else {
        form.address = {
            zip_code: "",
            street: "",
            number: "",
            complement: "",
            neighborhood: "",
            city: "",
            state: "",
            country: "BRA",
        };
    }

    if (pix) {
        form.pix = JSON.parse(pix);
    } else {
        form.pix = { document: "" };
    }

    if (credit_card) {
        const storedCard = JSON.parse(credit_card);
        form.credit_card = cardWithoutSecrets({
            ...form.credit_card,
            ...storedCard,
        });
    }

    if (billet) {
        form.billet = JSON.parse(billet);
    }

    if (urlDocument) {
        form.pix.document = urlDocument;
        form.credit_card.document = urlDocument;
        form.billet.document = urlDocument;
    }

    if (fieldConfigs.value.hideDocumentField) {
        const cpf = generateValidCpf();

        if(! form.pix.document || form.pix.document.length === 0) {
            form.pix.document = cpf;
        }

        if(! form.billet.document || form.billet.document.length === 0) {
            form.billet.document = cpf;
        }
    }
};

const fetchGeolocationForForm = () => {
    const offer = props.offer;

    if (!offer?.requires_address && !offer?.require_address_when_payment_method_is_credit_card) {
        if (typeof navigator !== 'undefined' && navigator.geolocation?.getCurrentPosition) {
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    form.latitude = pos.coords.latitude;
                    form.longitude = pos.coords.longitude;
                },
                () => {
                    geolocationAddressFailed.value = true;
                },
                { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
            );
        } else {
            geolocationAddressFailed.value = true;
        }
    }
};

onMounted(async () => {
    if (typeof window !== 'undefined' && window.__loadingMessageInterval) {
        clearInterval(window.__loadingMessageInterval);
        window.__loadingMessageInterval = null;
    }
    const ssrEl = document.getElementById('ssr-loading-message');
    if (ssrEl) ssrEl.remove();

    await updateLocale();
    fetchGeoLocation();

    formattedTime.value = formatTime(timeLeft.value);
    interval = setInterval(updateTime, 1000);

    applyCustomColors();

    purgeStoredCardSecrets();

    (props.pixels || []).forEach((pixel) => {
        if (pixel.pixelable_type === "App\\Models\\Product\\Pixel\\FacebookPixel") {
            if (mainFacebookPixel.value === null) {
                mainFacebookPixel.value = pixel.pixelable;
            }

            let customerData = { external_id: props.visit.uuid };
            if (props.visit.query_params.fbp !== null) {
                customerData.fbp = props.visit.query_params.fbp;
            }
            if (props.visit.query_params.fbc !== null) {
                customerData.fbc = props.visit.query_params.fbc;
            }

            initPixel({
                pixelId: pixel.pixelable.pixel_id,
                data: customerData,
            });
        }

        if (pixel.pixelable_type === "App\\Models\\Product\\Pixel\\KwaiPixel") {
            if (mainKwaiPixel.value === null) {
                mainKwaiPixel.value = pixel.pixelable;
            }

            let kwaiData = { external_id: props.visit.uuid };
            
            initKwaiPixel({
                pixelId: pixel.pixelable.pixel_id,
                data: kwaiData,
            });
        }

        if (pixel.pixelable_type === "App\\Models\\Product\\Pixel\\TiktokPixel") {
            if (mainTikTokPixel.value === null) {
                mainTikTokPixel.value = pixel.pixelable;
            }

            const options = {};
            if (pixel.pixelable.test_event_code) {
                options.test_event_code = pixel.pixelable.test_event_code;
            }

            initTikTokPixel({
                pixelId: pixel.pixelable.pixel_id,
                options: options,
            });
        }

        if (pixel.pixelable_type === "App\\Models\\Product\\Pixel\\GoogleTagManagerPixel") {
            // PAN/CVV ficam em input nosso. O container do lojista não entra nesta página.
            if (canLoadMerchantGtm(props.checkout) && mainGTMPixel.value === null) {
                mainGTMPixel.value = pixel.pixelable;
                initGTM(pixel.pixelable.container_id);
            }
        }
    });

    if (mainFacebookPixel.value) {
        dispatchPageViewEvent({ options: { eventID: props.visit.uuid } });

        if (mainFacebookPixel.value.events?.initiateCheckout) {
            dispatchFacebookPixelInitiateCheckoutEvent({
                options: { eventID: props.visit.uuid },
            });
        }
    }

    if (mainKwaiPixel.value) {
        if (mainKwaiPixel.value.events?.contentView) {
            dispatchKwaiContentViewEvent({
                options: { eventID: props.visit.uuid },
            });
        }

        if (mainKwaiPixel.value.events?.initiateCheckout) {
            dispatchKwaiInitiateCheckoutEvent({
                options: { eventID: props.visit.uuid },
            });
        }
    }

    if (mainTikTokPixel.value) {
        identifyTikTokUser({
            externalId: props.visit?.uuid?.trim() ?? "",
        });

        if (mainTikTokPixel.value.events?.viewContent) {
            dispatchTikTokViewContent({
                data: {
                    content_type: 'product',
                    content_id: props.product.uuid,
                },
                options: {
                    event_id: props.visit.uuid,
                },
            });
        }

        if (mainTikTokPixel.value.events?.initiateCheckout) {
            dispatchTikTokInitiateCheckout({
                data: {
                    content_type: 'product',
                    content_id: props.product.uuid,
                    value: getPrice(props.offer, form.payment_method) / 100,
                    currency: checkoutCurrency,
                },
                options: {
                    event_id: props.visit.uuid,
                },
            });
        }
    }

    if (mainGTMPixel.value) {
        if (mainGTMPixel.value.events?.pageView) {
            dispatchGTMPageView({
                pageTitle: `Checkout - ${props.product.name}`,
                pagePath: window.location.pathname,
            });
        }

        if (mainGTMPixel.value.events?.viewItem) {
            dispatchGTMViewItem({
                currency: checkoutCurrency,
                value: getPrice(props.offer, form.payment_method) / 100,
                items: [{
                    item_id: props.product.uuid,
                    item_name: props.product.name,
                    price: getPrice(props.offer, form.payment_method) / 100,
                    quantity: 1,
                }],
            });
        }

        if (mainGTMPixel.value.events?.beginCheckout) {
            dispatchGTMBeginCheckout({
                currency: checkoutCurrency,
                value: totalCart.value / 100,
                items: [{
                    item_id: props.product.uuid,
                    item_name: props.product.name,
                    price: getPrice(props.offer, form.payment_method) / 100,
                    quantity: 1,
                }],
            });
        }
    }

    checkFingerprintLoadedIntervalId.value = setInterval(checkFingerprintLoaded, 1000);

    loadingMessageIntervalId.value = setInterval(() => {
        loadingMessageIndex.value++;
    }, 3000);

    preFillFormFromLocalStorage();

    if (!initialized.value) {
        initialized.value = true;
    }

    if (typeof window !== 'undefined' && props.checkout?.back_redirect_url) {
        history.pushState(null, '', window.location.href);
        backRedirectHandler = () => {
            window.location.href = props.checkout.back_redirect_url;
        };
        window.addEventListener('popstate', backRedirectHandler);
    }
});

const dispatchedFingerprintLoaded = ref(false);

const checkFingerprintLoadedIntervalId = ref(null);

watch(() => dispatchedFingerprintLoaded.value, (newDispatchedFingerprintLoaded) => {
    if (newDispatchedFingerprintLoaded) {
        clearInterval(checkFingerprintLoadedIntervalId.value);
    }
}, { immediate: true });

const checkFingerprintLoaded = () => {
    if (dispatchedFingerprintLoaded.value) {
        return;
    }

    if (typeof window !== "undefined" && window.fingerprint) {
        updateFingerprint(window.fingerprint);
        dispatchedFingerprintLoaded.value = true;
    }
};

onBeforeUnmount(() => {
    clearInterval(interval);
    if (loadingMessageIntervalId.value) {
        clearInterval(loadingMessageIntervalId.value);
        loadingMessageIntervalId.value = null;
    }
    if (checkFingerprintLoadedIntervalId.value) {
        clearInterval(checkFingerprintLoadedIntervalId.value);
        checkFingerprintLoadedIntervalId.value = null;
    }
    if (backRedirectHandler) {
        window.removeEventListener('popstate', backRedirectHandler);
        backRedirectHandler = null;
    }
});
</script>

<style>
/* Estilos para contador expirado */
#countdown-container.expired {
    opacity: 0.7;
    background-color: #f3f4f6 !important;
}

#countdown-container.expired h3 {
    color: #6b7280 !important;
}

#countdown-container.expired #countdown {
    color: #9ca3af !important;
}

#countdown-container.expired p {
    color: #9ca3af !important;
}

/* Estilos para o indicador de configuração */
#countdown-container .absolute.top-2.right-2.pointer-events-none {
    transition: opacity 0.2s ease;
}

#countdown-container:hover .absolute.top-2.right-2.pointer-events-none {
    opacity: 0.8;
}

/* Melhorar a aparência do countdown */
#countdown-container {
    transition: all 0.3s ease;
}

#countdown-container:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Estilos para a seção de header do checkout */
.checkout-header-section {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}

/* Esconder elementos que não são contadores na seção de header */
.checkout-header-section > div:not([id*="countdown"]) {
    display: none;
}

/* Garantir que contadores sejam visíveis */
.checkout-header-section #countdown-container {
    display: block;
}

/* Estilos para compensar o header fixo */
body.has-countdown-header {
    padding-top: 4rem;
}

/* Estilos específicos para mobile */
@media (max-width: 768px) {
    body.has-countdown-header {
        padding-top: 5rem;
    }
}

#section-top-placeholder {
    display: none;
}

#section-bottom-placeholder {
    display: none;
}

/* Animação para box de erro */
@keyframes fade-in {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.animate-fade-in {
    animation: fade-in 0.3s ease-out;
}
</style>

<template>
    <Head>
        <title>Checkout - {{ product.name }}</title>
    </Head>

    <span
        v-if="checkoutCollectsCardData(checkout)"
        data-checkout-card-data
        hidden
    ></span>

    <!-- Componente de erro quando checkout está desativado -->
    <CheckoutDisabled
        v-if="checkoutDisabled"
        :message="checkoutErrorMessage"
    />

    <RecoveryOfferModal
        v-if="!checkoutDisabled && recoveryPrompt"
        :show="showRecoveryOfferModal"
        :prompt="recoveryPrompt"
        :payment-method="form.payment_method"
        @close="showRecoveryOfferModal = false"
    />

    <!-- Erro de processamento de pagamento (quando não é checkout desativado) -->
    <div class="px-5 pt-5" v-if="!checkoutDisabled && error?.message">
        <div class="max-w-2xl mx-auto bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-xl shadow-lg p-6 animate-fade-in">
            <div class="flex items-start flex-col gap-4">

                <!-- Conteúdo -->
                <div class="flex flex-col">
                    <!-- Ícone de erro -->
               <div class="flex flex-row gap-2">
                <div class="flex-shrink-0">
                    <div class="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                        <svg
                            class="w-7 h-7 text-red-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                    </div>
                </div>

                    <h3 class="text-red-800 text-xl font-grotesk65 mb-3">
                        Ocorreu um erro ao processar seu pagamento!
                    </h3>
               </div>

                    <p class="text-red-700 text-base font-grotesk55 mb-4">
                        Verifique os dados informados e tente novamente. Se o problema persistir, entre em contato com nosso suporte.
                    </p>


                </div>

                  <div class="bg-white/60 rounded-lg p-4 border border-red-100 w-full">
                        <p class="text-red-800 font-grotesk65 text-sm mb-2">Detalhes do erro:</p>
                        <ul class="space-y-2 text-red-700 text-sm font-grotesk55">
                            <li class="flex items-start gap-2">
                                <span class="text-red-500 mt-1">•</span>
                                <span>{{ error.message }}</span>
                            </li>
                        </ul>
                    </div>
            </div>
        </div>
    </div>

    <div>
        <Suspense>
            <component :is="templateComponent" @purchase="purchase($event)" />

            <template #fallback>
                <div class="w-full flex flex-col items-center py-5 lg:py-10 pb-20 custom-checkout-bg min-h-px">
                    <div class="w-full px-4 lg:px-0 flex flex-col lg:flex-row lg:justify-center lg:gap-6">
                        <div class="w-full lg:w-1/3 flex flex-col space-y-4">
                            <div v-if="product?.name" class="mt-5">
                                <h1 class="text-lg font-grotesk75">{{ product.name }}</h1>
                            </div>
                            <div v-else class="mt-5">
                                <div class="checkout-skeleton-shimmer h-6 w-[60%] rounded"></div>
                            </div>

                            <p class="text-sm text-gray-600 mb-4">
                                🔒 {{ currentLoadingMessage || t('checkout.please_wait') }}
                            </p>

                            <div class="w-full rounded-lg space-y-4">
                                <div class="grid grid-cols-1 gap-4">
                                    <div class="space-y-2">
                                        <div class="checkout-skeleton-shimmer h-3 w-16 rounded"></div>
                                        <div class="checkout-skeleton-shimmer h-10 w-full rounded"></div>
                                    </div>
                                    <div class="space-y-2">
                                        <div class="checkout-skeleton-shimmer h-3 w-20 rounded"></div>
                                        <div class="checkout-skeleton-shimmer h-10 w-full rounded"></div>
                                    </div>
                                    <div class="space-y-2">
                                        <div class="checkout-skeleton-shimmer h-3 w-24 rounded"></div>
                                        <div class="checkout-skeleton-shimmer h-10 w-full rounded"></div>
                                    </div>
                                </div>
                            </div>

                            <div class="w-full rounded-lg space-y-4">
                                <div class="flex gap-2 mb-4">
                                    <div class="checkout-skeleton-shimmer h-10 w-24 rounded"></div>
                                    <div class="checkout-skeleton-shimmer h-10 w-24 rounded"></div>
                                </div>
                                <div class="checkout-skeleton-shimmer h-5 w-1/3 rounded mb-2"></div>
                                <div class="checkout-skeleton-shimmer h-3 w-full rounded mb-4"></div>
                                <div class="checkout-skeleton-shimmer h-10 w-full rounded mb-4"></div>
                                <div class="checkout-skeleton-shimmer h-12 w-full rounded"></div>
                            </div>

                            <div class="checkout-skeleton-shimmer h-[200px] w-full rounded-lg"></div>
                        </div>
                    </div>
                </div>
            </template>
        </Suspense>
    </div>

    <WhatsAppFloat :product="product" :field-configs="fieldConfigs" />

    <!-- <LanguageSelector
        v-if="(detectedLanguage || geoDetectedLanguage) && supportedLanguages"
        :detected-language="geoDetectedLanguage || detectedLanguage"
        :supported-languages="supportedLanguages"
    /> -->

    <!-- Container para autenticação 3DS -->
    <div id="bpmpi-authentication-container"
         class="fixed inset-0 z-50 hidden bg-black/50 flex items-center justify-center">
        <div class="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-auto p-6 shadow-2xl">
            <div v-if="is3DSAuthenticating" class="text-center mb-4">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p class="text-sm text-gray-600">Autenticando transação...</p>
            </div>
            <div id="bpmpi-auth-iframe"></div>
        </div>
    </div>

    <!-- Mensagem de erro 3DS -->
    <div v-if="threeDSError" class="fixed bottom-4 right-4 z-50 bg-red-500 text-white px-6 py-4 rounded-lg shadow-lg max-w-md">
        <div class="flex items-start gap-3">
            <svg class="w-6 h-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
                <p class="font-semibold">Erro na Autenticação 3DS</p>
                <p class="text-sm mt-1">{{ threeDSError }}</p>
                <button @click="threeDSError = null" class="text-sm underline mt-2">Fechar</button>
            </div>
        </div>
    </div>

</template>
